import { z } from 'zod';
import { ConsentType } from '../enums';
import { passwordSchema } from './common';
import { supportedLanguages } from './languages';

export const updateAccountSchema = z
  .object({
    name: z.string().trim().min(1).max(80).nullable().optional(),
    /**
     * Язык интерфейса. Весь список, а не два языка: пользователь выбирает
     * из десяти, и урезанный набор здесь означал бы, что выбор украинского
     * до сервера не доходит — а письма отправляются по этому полю.
     */
    locale: z.enum(supportedLanguages as [string, ...string[]]).optional(),
  })
  .strict();
export type UpdateAccountInput = z.infer<typeof updateAccountSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Введите текущий пароль'),
    newPassword: passwordSchema,
  })
  .strict();

/** §49: пользователь может удалить данные по частям, а не только аккаунт целиком. */
export const DeletionScope = {
  AVATAR: 'avatar',
  VOICE: 'voice',
  PHOTOS: 'photos',
  SOURCE_VIDEOS: 'source_videos',
  PERSONA: 'persona',
  ACCOUNT: 'account',
} as const;
export type DeletionScope = (typeof DeletionScope)[keyof typeof DeletionScope];

export const deletionScopeLabels: Record<DeletionScope, string> = {
  avatar: 'Аватар',
  voice: 'Голос',
  photos: 'Фотографии',
  source_videos: 'Исходные видео',
  persona: 'Вся персона',
  account: 'Аккаунт',
};

export const requestDeletionSchema = z
  .object({
    scope: z.enum(Object.values(DeletionScope) as [string, ...string[]]),
    /** Для удаления аккаунта требуем пароль — действие необратимо. */
    password: z.string().min(1).optional(),
  })
  .strict();

export const acceptConsentsSchema = z
  .object({
    acceptedConsents: z.record(
      z.enum(Object.values(ConsentType) as [string, ...string[]]),
      z.string().min(1),
    ),
  })
  .strict();

export interface ConsentDocumentView {
  type: ConsentType;
  version: string;
  title: string;
  summary: string;
  url: string | null;
}

/**
 * Полный текст документа. Отдельно от списка: форме регистрации нужны только
 * заголовки, а тексты вместе весят десятки килобайт на каждое открытие.
 */
export interface ConsentDocumentText {
  type: ConsentType;
  version: string;
  title: string;
  /** Markdown. Локаль выбрана сервером: запрошенная либо запасная. */
  body: string;
  /** На каком языке текст на самом деле — запрошенного перевода может не быть. */
  locale: string;
}

export interface ConsentRecordView {
  type: ConsentType;
  version: string;
  acceptedAt: string;
  revokedAt: string | null;
  isCurrent: boolean;
}

export interface DeletionRequestView {
  id: string;
  scope: DeletionScope;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requestedAt: string;
  completedAt: string | null;
}
